import { Link } from "@tanstack/react-router";
import { Phone, MessageCircle, MapPin, Star, ShieldCheck, Calendar, ArrowRight } from "lucide-react";
import { getBusinessConfig } from "@/lib/site-config";
import { trackEvent } from "@/lib/tracking";

/**
 * Compact "trusted local garage in E14" trust bar for visitors arriving from
 * Google Maps. Reinforces DVSA station number, MOT classes, local reviews and
 * the four fastest actions: Call / WhatsApp / Booking / Directions.
 *
 * Tracks `homepage_local_pack_cta_click` with `cta_type` + `source_page="/"`.
 */
export function LocalTrustBar() {
  const biz = getBusinessConfig();
  const fire = (cta_type: string) =>
    trackEvent("homepage_local_pack_cta_click", { cta_type, source_page: "/" });

  return (
    <section
      aria-labelledby="local-trust-heading"
      className="border-y border-border bg-gradient-to-b from-secondary/40 to-background"
    >
      <div className="mx-auto max-w-6xl px-4 md:px-6 py-10 md:py-12">
        <div className="grid gap-8 lg:grid-cols-[1.1fr_auto] lg:items-center">
          <div>
            <p className="text-[11px] font-bold tracking-[0.22em] uppercase text-primary">
              Trusted local garage in E14
            </p>
            <h2 id="local-trust-heading" className="mt-2 text-2xl md:text-3xl font-extrabold text-foreground tracking-tight">
              You've found the right Poplar garage.
            </h2>
            <p className="mt-3 text-sm md:text-[15px] text-muted-foreground leading-relaxed max-w-2xl">
              Real workshop, real local mechanics, real reviews. Call, WhatsApp or request a booking — we'll come back to confirm the same working day.
            </p>

            <ul className="mt-5 grid gap-2.5 sm:grid-cols-2 text-sm">
              <li className="flex items-start gap-2.5 text-foreground/85">
                <ShieldCheck className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                <span>DVSA-authorised MOT station · <span className="font-bold">V106376</span></span>
              </li>
              <li className="flex items-start gap-2.5 text-foreground/85">
                <ShieldCheck className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                <span>MOT classes <span className="font-bold">1, 2, 3, 4, 5 &amp; 7</span></span>
              </li>
              <li className="flex items-start gap-2.5 text-foreground/85">
                <Star className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                <span>4.4 rating from <span className="font-bold">116+</span> Google reviews</span>
              </li>
              <li className="flex items-start gap-2.5 text-foreground/85">
                <MapPin className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                <span>Real workshop in <span className="font-bold">Poplar, E14</span></span>
              </li>
            </ul>

            <div className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-muted-foreground">
              <Link
                to="/official-business-information"
                onClick={() => fire("official_info_link")}
                className="inline-flex items-center gap-1 text-primary font-bold hover:underline"
              >
                Verify business details <ArrowRight className="h-3 w-3" />
              </Link>
              <span className="text-border" aria-hidden="true">·</span>
              <Link
                to="/reviews"
                onClick={() => fire("reviews_link")}
                className="inline-flex items-center gap-1 text-primary font-bold hover:underline"
              >
                Read reviews <ArrowRight className="h-3 w-3" />
              </Link>
              <span className="text-border" aria-hidden="true">·</span>
              <Link
                to="/contact"
                onClick={() => fire("contact_link")}
                className="inline-flex items-center gap-1 text-primary font-bold hover:underline"
              >
                Contact &amp; directions <ArrowRight className="h-3 w-3" />
              </Link>
            </div>
          </div>

          <div className="flex flex-col gap-2 min-w-[240px]">
            <a
              href={biz.phoneHref}
              onClick={() => fire("call")}
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-primary px-5 py-3 text-sm font-bold text-primary-foreground hover:bg-primary/90 shadow-lg shadow-primary/20"
            >
              <Phone className="h-4 w-4" /> Call {biz.phone}
            </a>
            <a
              href={biz.whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => fire("whatsapp")}
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-[#25D366] px-5 py-3 text-sm font-bold text-white hover:bg-[#1ea952]"
            >
              <MessageCircle className="h-4 w-4" /> WhatsApp
            </a>
            <Link
              to="/book"
              onClick={() => fire("book")}
              className="inline-flex items-center justify-center gap-2 rounded-lg border border-border bg-card px-5 py-3 text-sm font-bold text-foreground hover:border-primary/40"
            >
              <Calendar className="h-4 w-4" /> Request booking
            </Link>
            <a
              href={biz.googleMapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => fire("directions")}
              className="inline-flex items-center justify-center gap-2 rounded-lg border border-border bg-background px-5 py-3 text-sm font-bold text-foreground hover:border-primary/40"
            >
              <MapPin className="h-4 w-4" /> Get directions
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
